import React from 'react';
import {
  StyleSheet, Text, useWindowDimensions, View,
} from 'react-native';
import Colors from '../../constants/Colors';
import ScrollViewStyled from '../ScrollViewStyled';

interface Props {
  data: any;
  loading: boolean;
}

const IntEnvWidget = (props: Props) => {
  const { width, height } = useWindowDimensions();
  const isSplitScreen1080p = width === 960 && height === 1080;
  const isQuarterResolution = width === 960 && height === 540;

  // Determine metric value font size based on screen height
  const getMetricValueFontSize = () => {
    if (isSplitScreen1080p) return 22;
    if (isQuarterResolution) return 14;
    if (height <= 540) return 15;
    if (height <= 720 || width <= 440) return 21;
    if (height <= 1080) return 32;
    if (height <= 1440) return 44;
    return 64; // For height > 1440 (up to 2160)
  };

  const getMetricTitleMarginBottom = () => {
    if (isSplitScreen1080p) return 10;
    if (isQuarterResolution) return 5;
    if (height <= 540) return 10;
    if (height <= 720) return 11;
    if (height <= 1080) return 20;
    if (height <= 1440) return 30;
    return 54; // For height > 1440 (up to 2160)
  };

  const getMetricTitleFontSize = () => {
    if (isSplitScreen1080p) return 16;
    if (isQuarterResolution) return 10;
    if (height <= 540) return 12;
    if (height <= 720 || width <= 440) return 14;
    if (height <= 1080) return 22;
    if (height <= 1440) return 29;
    return 44; // For height > 1440 (up to 2160)
  };

  const getMetricUnitFontSize = () => {
    if (isSplitScreen1080p) return 12;
    if (isQuarterResolution) return 8;
    if (height <= 540) return 10;
    if (height <= 720 || width <= 440) return 12;
    if (height <= 1080) return 18;
    if (height <= 1440) return 24;
    return 36; // For height > 1440 (up to 2160)
  };

  const getMetricItemPadding = () => {
    if (isSplitScreen1080p) return 12;
    if (isQuarterResolution) return 5;
    if (height <= 540) return 10;
    if (height <= 720) return 12;
    if (height <= 1080) return 20;
    if (height <= 1440) return 28;
    return 50; // For height > 1440 (up to 2160)
  };

  const getMetricItemMargin = () => {
    if (isSplitScreen1080p) return 5;
    if (isQuarterResolution) return 2;
    if (height <= 540) return 2;
    if (height <= 720) return 3;
    if (height <= 1080) return 8;
    if (height <= 1440) return 12;
    return 18; // For height > 1440 (up to 2160)
  };

  const metricItem = (title, value, label) => (
    <View style={[styles.metricItem,
      { padding: getMetricItemPadding(), margin: getMetricItemMargin() }]}>
      <View style={{ flexDirection: 'row' }}>
        <Text
          style={[
            styles.metricTitle,
            {
              marginBottom: getMetricTitleMarginBottom(),
              fontSize: getMetricTitleFontSize(),
            },
          ]}
        >
          {title}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
        <Text style={[styles.metricValue, { fontSize: getMetricValueFontSize() }]}>
          {value.toFixed(0)}
        </Text>
        <Text style={[styles.metricUnit, { fontSize: getMetricUnitFontSize() }]}>{label}</Text>
      </View>
    </View>
  );

  // eslint-disable-next-line no-unused-vars
  const peak = props.data?.peak ?? 200;

  const renderLayout = () => {
    if (isSplitScreen1080p) {
      return (
        <View style={styles.gridContainer}>
          <View style={styles.gridRow}>
            {metricItem('CO2', props?.data?.co2 ?? 0, 'ppm')}
            {props?.data?.noise ? metricItem('Noise', props?.data?.noise ?? 0, 'db') : null}
            {metricItem('TVOC', props?.data?.tvoc ?? 0, 'ppb')}
            {metricItem('Humidity', props?.data?.humidity ?? 0, '%')}
          </View>
          <View style={styles.gridRow}>
            {props?.data?.light ? metricItem('Light', props?.data?.light ?? 0, 'lx') : null}
            {metricItem('PM2.5', props?.data?.pm25 ?? 0, 'µg/m3')}
            {metricItem('PM10', props?.data?.pm10 ?? 0, 'µg/m3')}
            {metricItem('Temperature', props?.data?.temperature ?? 0, '°C')}
            {metricItem('Building Occupancy', props?.data?.occupancy ?? 0, 'People')}
          </View>
        </View>
      );
    }

    return (
      <View style={{ flex: 1 }}>
      <View style={{ flexDirection: 'row' }}>
        <View style={styles.metricsContainer}>
          {metricItem('CO2', props?.data?.co2 ?? 0, 'ppm')}
          {props?.data?.noise ? metricItem('Noise', props?.data?.noise ?? 0, 'db') : null}
          {metricItem('TVOC', props?.data?.tvoc ?? 0, 'ppb')}
          {metricItem('Humidity', props?.data?.humidity ?? 0, '%')}
        </View>
        <View style={styles.metricsContainer}>
          {props?.data?.light ? metricItem('Light', props?.data?.light ?? 0, 'lx') : null}
          {metricItem('PM2.5', props?.data?.pm25 ?? 0, 'µg/m3')}
          {metricItem('PM10', props?.data?.pm10 ?? 0, 'µg/m3')}
          {metricItem('Temperature', props?.data?.temperature ?? 0, '°C')}
        </View>
      </View>
        {metricItem('Building Occupancy', props?.data?.occupancy ?? 0, 'People')}
      </View>
    );
  };

  return (<ScrollViewStyled style={styles.container}>
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
    </View>
    {renderLayout()}
  </ScrollViewStyled>);
};

export default IntEnvWidget;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  changeText: {
    marginVertical: 8,
    marginRight: 4,
    marginLeft: 2,
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
  },
  shortScreenTitle: {
    fontSize: 16,
  },
  metricTitle: {
    fontFamily: 'Poppins_400Regular',
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
  },
  count: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 34,
    lineHeight: 42,
    color: Colors.light.hereworksBlack600,
  },
  metricItem: {
    flex: 1,
    borderRadius: 8,
    backgroundColor: Colors.light.hereworksWhite100,
    justifyContent: 'center',
  },
  liveCircle: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: Colors.light.hereworksRed500,
    borderWidth: 3,
    borderColor: Colors.light.hereworksLightPink,
    marginRight: 12,
  },
  metricsContainer: {
    flex: 1,
    justifyContent: 'space-evenly',
    alignContent: 'space-between',
  },
  gridContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  gridRow: {
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'space-between',
  },
  metricValue: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 32,
    lineHeight: 24,
    color: Colors.light.hereworksGreen400,
  },
  metricUnit: {
    fontFamily: 'Poppins_400Regular',
    lineHeight: 18,
    marginLeft: 8,
  },
});
